import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

const GUEST_MODE_KEY = 'wellness_cowgirl_guest_mode';

export interface ShowcaseSlide {
  id: string;
  title: string;
  description: string;
  component: 'event-card' | 'rsvp-form' | 'buddy-match' | 'chat-preview' | 'welcome' | 'herds' | 'friends' | 'finale' | 'event-types' | 'tiers' | 'legendary-teaser' | 'megan-welcome';
}

export interface TourStep {
  id: string;
  type?: 'fullscreen' | 'spotlight' | 'showcase';
  targetId?: string;
  route?: string;
  title: string;
  description?: string;
  details?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  cta?: { label: string; route: string };
  showcaseSlides?: ShowcaseSlide[];
  highlightTab?: 'home' | 'events' | 'social' | 'herds' | 'profile';
}

interface TourState {
  isActive: boolean;
  currentStepIndex: number;
  showPrompt: boolean;
  tourDismissed: boolean;
  taskGuideActive: boolean;
  taskGuideId: string | null;
  taskGuideStepIndex: number;
  startTour: () => void;
  replayTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  endTour: () => void;
  dismissTour: (dontShowAgain: boolean) => void;
  setShowPrompt: (show: boolean) => void;
  initializeTourState: (dismissed: boolean) => void;
  startTaskGuide: (taskId: string) => void;
  nextTaskStep: () => void;
  endTaskGuide: () => void;
}

export const useTourStore = create<TourState>((set) => ({
  isActive: false,
  currentStepIndex: 0,
  showPrompt: false,
  tourDismissed: false,
  taskGuideActive: false,
  taskGuideId: null,
  taskGuideStepIndex: 0,

  startTour: () => {
    set({ isActive: true, currentStepIndex: 0, showPrompt: false });
  },

  replayTour: () => {
    // Allow replaying the tour even if previously dismissed
    set({ isActive: true, currentStepIndex: 0, showPrompt: false, tourDismissed: false });
  },

  nextStep: () => {
    set((state) => ({ currentStepIndex: state.currentStepIndex + 1 }));
  },

  previousStep: () => {
    set((state) => ({ 
      currentStepIndex: Math.max(0, state.currentStepIndex - 1) 
    }));
  },

  endTour: async () => {
    set({ isActive: false, currentStepIndex: 0, showPrompt: false });
    
    // Mark tour as completed in database
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('profiles')
        .update({ tour_completed_at: new Date().toISOString() })
        .eq('id', user.id);
    }
  },

  dismissTour: async (dontShowAgain: boolean) => {
    // PERSISTENCE-FIRST: Save to storage BEFORE updating state
    if (dontShowAgain) {
      try {
        const isGuest = localStorage.getItem(GUEST_MODE_KEY) === 'true';
        if (isGuest) {
          localStorage.setItem('tour_dismissed_guest', 'true');
        } else {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // User-scoped localStorage key
            localStorage.setItem(`tour_dismissed_${user.id}`, 'true');

            const { error } = await supabase
              .from('profiles')
              .update({ tour_dismissed: true })
              .eq('id', user.id);
            
            if (error) {
              console.error('[useTour] Failed to persist tour dismissal to database:', error);
            }
          }
        }
      } catch (err) {
        console.error('[useTour] Error during tour dismissal persistence:', err);
      }
    }

    set({ 
      showPrompt: false, 
      tourDismissed: dontShowAgain,
      isActive: false 
    });
  },

  setShowPrompt: (show: boolean) => {
    set({ showPrompt: show });
  },

  initializeTourState: (dismissed: boolean) => {
    set({ tourDismissed: dismissed });
  },

  startTaskGuide: (taskId: string) => {
    set({ 
      taskGuideActive: true, 
      taskGuideId: taskId, 
      taskGuideStepIndex: 0 
    });
  },

  nextTaskStep: () => {
    set((state) => ({ 
      taskGuideStepIndex: state.taskGuideStepIndex + 1 
    }));
  },

  endTaskGuide: () => {
    set({ 
      taskGuideActive: false, 
      taskGuideId: null, 
      taskGuideStepIndex: 0 
    });
  },
}));
